import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  Upload, 
  Button, 
  message, 
  Space,
  Typography,
  Divider,
  Select
} from 'antd';
import { 
  DownloadOutlined, 
  QuestionCircleOutlined,
  CloudUploadOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { useXsjApi } from './useXsjApi';
import { XsjReportProps } from './types';

const { Title } = Typography;

const XsjReport: React.FC<XsjReportProps> = ({ username, displayName, isLoggedIn }) => {
  const [form] = Form.useForm();
  const [excelFileList, setExcelFileList] = useState<UploadFile[]>([]);
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);
  const { submitLoading, handleSubmit } = useXsjApi(username, displayName);

  // Excel文件上传配置
  const excelUploadProps: UploadProps = {
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel' ||
                     file.name.endsWith('.xlsx') ||
                     file.name.endsWith('.xls');
      if (!isExcel) {
        message.error('只能上传Excel文件（.xlsx或.xls）');
        return false;
      }
      const isLt25M = file.size / 1024 / 1024 < 25;
      if (!isLt25M) {
        message.error('文件大小不能超过25MB');
        return false;
      }
      // 将 File 对象包装成 UploadFile 对象
      const uploadFile: UploadFile = {
        uid: `excel-${Date.now()}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      setExcelFileList([uploadFile]);
      return false;
    },
    fileList: excelFileList,
    onRemove: () => {
      setExcelFileList([]);
      return true;
    },
    maxCount: 1,
    accept: '.xlsx,.xls'
  };

  // 图片文件上传配置
  const imageUploadProps: UploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/') || 
                     /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name);
      if (!isImage) {
        message.error('只能上传图片文件');
        return false;
      }
      // 检查单张图片大小（不能超过1MB）
      const isLt1M = file.size / 1024 / 1024 < 1;
      if (!isLt1M) {
        message.error(`图片 "${file.name}" 大小超过1MB，请选择更小的图片`);
        return false;
      }
      
      // 检查所有图片总大小（不能超过10MB）
      const currentTotalSize = imageFileList.reduce((total, item) => {
        return total + (item.originFileObj?.size || 0);
      }, 0);
      const newTotalSize = currentTotalSize + file.size;
      const isTotalLt10M = newTotalSize / 1024 / 1024 < 10;
      if (!isTotalLt10M) {
        message.error('所有图片总大小不能超过10MB，请删除部分图片或选择更小的图片');
        return false;
      }
      
      // 将 File 对象包装成 UploadFile 对象
      const uploadFile: UploadFile = {
        uid: `image-${Date.now()}-${Math.random()}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      setImageFileList((prev) => [...prev, uploadFile]);
      return false;
    },
    fileList: imageFileList,
    onRemove: (file) => {
      setImageFileList((prev) => prev.filter((item) => item.uid !== file.uid));
      return true;
    },
    multiple: true,
    accept: 'image/*'
  };

  // 处理表单提交
  const onFinish = async (values: any) => {
    if (!excelFileList.length) {
      message.error('请上传Excel文件');
      return;
    }

    if (!imageFileList.length) {
      message.error('请上传图片文件');
      return;
    }

    const excelFile = excelFileList[0].originFileObj as File;
    const imageFiles = imageFileList.map((item) => item.originFileObj as File).filter(Boolean);

    // 如果任务名为空，生成默认任务名
    let taskName = values.taskName?.trim();
    if (!taskName) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const timestamp = `${year}${month}${day}-${hours}${minutes}${seconds}`;
      taskName = `西山居报告-${timestamp}`;
    }

    // 报告类型映射
    const reportTypeMap: { [key: string]: string } = {
      '西山居（完整版）': 'shangyi_report',
      '剑网3': 'shangyi_report_k1',
      '尘白禁区': 'shangyi_report_k3',
      '解限机': 'shangyi_report_k8'
    };
    const reportType = reportTypeMap[values.reportType] || 'shangyi_report';

    const success = await handleSubmit(
      taskName,
      excelFile,
      imageFiles,
      reportType
    );

    if (success) {
      form.resetFields();
      setExcelFileList([]);
      setImageFileList([]);
    }
  };

  // 重置表单
  const handleReset = () => {
    form.resetFields();
    setExcelFileList([]);
    setImageFileList([]);
  };

  // 模板下载
  const handleTemplateDownload = () => {
    const downloadUrl = 'https://file.fakedata.cn/xsj/xsj%E6%8A%A5%E5%91%8A%E4%BC%A0%E8%BE%93%E6%A8%A1%E6%9D%BF.zip';
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'xsj报告传输模板.zip';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('模板下载已开始');
  };

  // 使用说明
  const handleUsageGuide = () => {
    message.info('使用说明功能开发中');
  };

  if (!isLoggedIn) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>请先登录</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* 顶部标题和操作栏 */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <Title level={3} style={{ margin: 0 }}>西山居报告传输</Title>
        <Space>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={handleTemplateDownload}
          >
            模板下载
          </Button>
          <Button 
            icon={<QuestionCircleOutlined />} 
            onClick={handleUsageGuide}
          >
            使用说明
          </Button>
        </Space>
      </div>

      <Divider />

      {/* 表单区域 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ maxWidth: '800px' }}
      >
        {/* 任务名输入 */}
        <Form.Item
          label="任务名"
          name="taskName"
        >
          <Input 
            placeholder="请输入任务名（不填写将自动生成）" 
            suffix={<QuestionCircleOutlined style={{ color: '#999' }} />}
          />
        </Form.Item>

        {/* 报告类型选择 */}
        <Form.Item
          label="报告类型"
          name="reportType"
          initialValue="西山居（完整版）"
          rules={[{ required: true, message: '请选择报告类型' }]}
        >
          <Select>
            <Select.Option value="西山居（完整版）">西山居（完整版）</Select.Option>
            <Select.Option value="剑网3">剑网3</Select.Option>
            <Select.Option value="尘白禁区">尘白禁区</Select.Option>
            <Select.Option value="解限机">解限机</Select.Option>
          </Select>
        </Form.Item>

        {/* Excel文件上传 */}
        <Form.Item
          label={
            <span>
              * 上传文件
              <span style={{ fontSize: '70%', marginLeft: '8px', color: '#ff4d4f' }}>
                （提示："章节名称"、"当前章节页码"、"图片名称"、"时间"字段必填）
              </span>
            </span>
          }
          required
          rules={[{ required: true, message: '请上传Excel文件' }]}
        >
          <Upload.Dragger {...excelUploadProps}>
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">点击或将文件拖拽到此处进行上传</p>
          </Upload.Dragger>
        </Form.Item>

        {/* 图片文件上传 */}
        <Form.Item
          label={
            <span>
              * 上传图片
              <span style={{ fontSize: '70%', marginLeft: '8px', color: '#ff4d4f' }}>
                （提示：所有图片总大小不能超10Mb，单张图片大小不能超1Mb）
              </span>
            </span>
          }
          required
          rules={[{ required: true, message: '请上传图片文件' }]}
        >
          <Upload.Dragger {...imageUploadProps}>
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">点击或将文件拖拽到此处进行上传</p>
          </Upload.Dragger>
        </Form.Item>

        {/* 操作按钮 */}
        <Form.Item style={{ marginTop: '32px', marginBottom: 0, textAlign: 'center' }}>
          <Space>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={submitLoading}
              size="large"
            >
              提交
            </Button>
            <Button 
              htmlType="button"
              onClick={handleReset}
              size="large"
            >
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default XsjReport;

